/* eslint-disable react-refresh/only-export-components, max-lines */
import {
  DimeField,
  DimeFieldDescription,
  DimeFieldLabel,
  DimeInputGroup,
  DimeInputGroupAddon,
  DimeInputGroupButton,
  DimeInputGroupInput,
  DimeInputGroupText,
  DimeInputGroupTextarea,
  DimeSeparator,
  DimeSpinner,
} from '@nudool/design-system';
import { createFileRoute } from '@tanstack/react-router';
import { Copy, Info, Search, Send } from 'lucide-react';
import * as React from 'react';

export const Route = createFileRoute('/design-system/input-group')({
  component: InputGroupDocs,
});

function DocsSection({ title, children }: Readonly<{ title: string; children: React.ReactNode }>) {
  return (
    <section className="mb-10">
      <h2 className="mb-1 text-xl font-semibold">{title}</h2>
      <DimeSeparator className="mb-4" />
      {children}
    </section>
  );
}

function Preview({ children }: Readonly<{ children: React.ReactNode }>) {
  return (
    <div className="flex flex-wrap items-start justify-center gap-4 rounded-lg border border-border bg-background p-6">
      {children}
    </div>
  );
}

// ── Align variants ─────────────────────────────────────────────────────────────

function AlignInlineStart() {
  return (
    <DimeField className="w-full max-w-sm">
      <DimeFieldLabel htmlFor="ig-align-inline-start">Search</DimeFieldLabel>
      <DimeInputGroup>
        <DimeInputGroupAddon align="inline-start">
          <Search />
        </DimeInputGroupAddon>
        <DimeInputGroupInput id="ig-align-inline-start" placeholder="Search…" />
      </DimeInputGroup>
      <DimeFieldDescription>Icon positioned at the start of the input.</DimeFieldDescription>
    </DimeField>
  );
}

function AlignInlineEnd() {
  return (
    <DimeField className="w-full max-w-sm">
      <DimeFieldLabel htmlFor="ig-align-inline-end">Password</DimeFieldLabel>
      <DimeInputGroup>
        <DimeInputGroupInput
          id="ig-align-inline-end"
          type="password"
          placeholder="Enter password…"
        />
        <DimeInputGroupAddon align="inline-end">
          <Info />
        </DimeInputGroupAddon>
      </DimeInputGroup>
      <DimeFieldDescription>Icon positioned at the end of the input.</DimeFieldDescription>
    </DimeField>
  );
}

function AlignBlockStart() {
  return (
    <DimeField className="w-full max-w-sm">
      <DimeFieldLabel htmlFor="ig-align-block-start">Notes</DimeFieldLabel>
      <DimeInputGroup>
        <DimeInputGroupAddon align="block-start">
          <DimeInputGroupText>Plain text</DimeInputGroupText>
          <DimeInputGroupButton size="icon-xs" aria-label="Copy">
            <Copy />
          </DimeInputGroupButton>
        </DimeInputGroupAddon>
        <DimeInputGroupTextarea id="ig-align-block-start" placeholder="Add a note…" rows={3} />
      </DimeInputGroup>
      <DimeFieldDescription>Toolbar positioned above the textarea.</DimeFieldDescription>
    </DimeField>
  );
}

function AlignBlockEnd() {
  return (
    <DimeField className="w-full max-w-sm">
      <DimeFieldLabel htmlFor="ig-align-block-end">Message</DimeFieldLabel>
      <DimeInputGroup>
        <DimeInputGroupTextarea id="ig-align-block-end" placeholder="Type a message…" rows={3} />
        <DimeInputGroupAddon align="block-end">
          <DimeInputGroupButton size="icon-xs" aria-label="Send">
            <Send />
          </DimeInputGroupButton>
          <DimeInputGroupButton size="sm" className="ml-auto">
            Submit
          </DimeInputGroupButton>
        </DimeInputGroupAddon>
      </DimeInputGroup>
      <DimeFieldDescription>Toolbar positioned below the textarea.</DimeFieldDescription>
    </DimeField>
  );
}

// ── Examples ───────────────────────────────────────────────────────────────────

function IconExample() {
  return (
    <div className="flex flex-col gap-4">
      <DimeField className="w-full max-w-sm">
        <DimeFieldLabel htmlFor="ig-icon-search">Search</DimeFieldLabel>
        <DimeInputGroup>
          <DimeInputGroupAddon align="inline-start">
            <Search />
          </DimeInputGroupAddon>
          <DimeInputGroupInput id="ig-icon-search" placeholder="Search parts…" />
        </DimeInputGroup>
      </DimeField>
      <DimeField className="w-full max-w-sm">
        <DimeFieldLabel htmlFor="ig-icon-email">Email</DimeFieldLabel>
        <DimeInputGroup>
          <DimeInputGroupInput id="ig-icon-email" type="email" placeholder="name@example.com" />
          <DimeInputGroupAddon align="inline-end">
            <Info />
          </DimeInputGroupAddon>
        </DimeInputGroup>
      </DimeField>
    </div>
  );
}

function TextExample() {
  return (
    <div className="flex flex-col gap-4">
      <DimeField className="w-full max-w-sm">
        <DimeFieldLabel htmlFor="ig-text-url">Website</DimeFieldLabel>
        <DimeInputGroup>
          <DimeInputGroupAddon align="inline-start">
            <DimeInputGroupText>https://</DimeInputGroupText>
          </DimeInputGroupAddon>
          <DimeInputGroupInput id="ig-text-url" placeholder="example.com" />
        </DimeInputGroup>
      </DimeField>
      <DimeField className="w-full max-w-sm">
        <DimeFieldLabel htmlFor="ig-text-weight">Weight</DimeFieldLabel>
        <DimeInputGroup>
          <DimeInputGroupInput id="ig-text-weight" type="number" placeholder="0.00" />
          <DimeInputGroupAddon align="inline-end">
            <DimeInputGroupText>kg</DimeInputGroupText>
          </DimeInputGroupAddon>
        </DimeInputGroup>
      </DimeField>
      <DimeField className="w-full max-w-sm">
        <DimeFieldLabel htmlFor="ig-text-url-both">Website</DimeFieldLabel>
        <DimeInputGroup>
          <DimeInputGroupAddon align="inline-start">
            <DimeInputGroupText>https://</DimeInputGroupText>
          </DimeInputGroupAddon>
          <DimeInputGroupInput id="ig-text-url-both" placeholder="example" />
          <DimeInputGroupAddon align="inline-end">
            <DimeInputGroupText>.com</DimeInputGroupText>
          </DimeInputGroupAddon>
        </DimeInputGroup>
      </DimeField>
    </div>
  );
}

function ButtonExample() {
  return (
    <div className="flex flex-col gap-4">
      <DimeField className="w-full max-w-sm">
        <DimeFieldLabel htmlFor="ig-btn-sku">SKU</DimeFieldLabel>
        <DimeInputGroup>
          <DimeInputGroupInput id="ig-btn-sku" placeholder="Enter SKU…" />
          <DimeInputGroupAddon align="inline-end">
            <DimeInputGroupButton size="sm">Look up</DimeInputGroupButton>
          </DimeInputGroupAddon>
        </DimeInputGroup>
      </DimeField>
      <DimeField className="w-full max-w-sm">
        <DimeFieldLabel htmlFor="ig-btn-copy">API Key</DimeFieldLabel>
        <DimeInputGroup>
          <DimeInputGroupInput id="ig-btn-copy" placeholder="sk-…" />
          <DimeInputGroupAddon align="inline-end">
            <DimeInputGroupButton size="icon-xs" aria-label="Copy">
              <Copy />
            </DimeInputGroupButton>
          </DimeInputGroupAddon>
        </DimeInputGroup>
      </DimeField>
    </div>
  );
}

function KbdExample() {
  return (
    <DimeField className="w-full max-w-sm">
      <DimeFieldLabel htmlFor="ig-kbd-search">Search</DimeFieldLabel>
      <DimeInputGroup>
        <DimeInputGroupAddon align="inline-start">
          <Search />
        </DimeInputGroupAddon>
        <DimeInputGroupInput id="ig-kbd-search" placeholder="Search…" />
        <DimeInputGroupAddon align="inline-end">
          <kbd className="rounded border border-border bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
            ⌘K
          </kbd>
        </DimeInputGroupAddon>
      </DimeInputGroup>
    </DimeField>
  );
}

function SpinnerExample() {
  return (
    <div className="flex flex-col gap-4">
      <DimeField className="w-full max-w-sm">
        <DimeFieldLabel htmlFor="ig-spinner-loading">Username</DimeFieldLabel>
        <DimeInputGroup>
          <DimeInputGroupInput id="ig-spinner-loading" placeholder="Test User" />
          <DimeInputGroupAddon align="inline-end">
            <DimeSpinner className="size-3" />
          </DimeInputGroupAddon>
        </DimeInputGroup>
        <DimeFieldDescription>Checking availability…</DimeFieldDescription>
      </DimeField>
    </div>
  );
}

function TextareaExample() {
  return (
    <DimeField className="w-full max-w-sm">
      <DimeFieldLabel htmlFor="ig-textarea-message">Message</DimeFieldLabel>
      <DimeInputGroup>
        <DimeInputGroupTextarea id="ig-textarea-message" placeholder="Type a message…" rows={3} />
        <DimeInputGroupAddon align="block-end">
          <DimeInputGroupButton size="icon-xs" aria-label="Send">
            <Send />
          </DimeInputGroupButton>
          <DimeInputGroupButton size="sm" className="ml-auto">
            Submit
          </DimeInputGroupButton>
        </DimeInputGroupAddon>
      </DimeInputGroup>
    </DimeField>
  );
}

// ── Page ───────────────────────────────────────────────────────────────────────

function InputGroupDocs() {
  return (
    <div>
      <h1 className="mb-1 text-3xl font-bold">Input Group</h1>
      <p className="mb-8 text-muted-foreground">
        Add addons, buttons, and helper content to inputs.
      </p>

      <DocsSection title="inline-start">
        <p className="mt-3 text-sm text-muted-foreground">
          Use <code>align="inline-start"</code> to position the addon at the start of the input.
          This is the default.
        </p>
        <Preview>
          <AlignInlineStart />
        </Preview>
      </DocsSection>

      <DocsSection title="inline-end">
        <p className="mt-3 text-sm text-muted-foreground">
          Use <code>align="inline-end"</code> to position the addon at the end of the input.
        </p>
        <Preview>
          <AlignInlineEnd />
        </Preview>
      </DocsSection>

      <DocsSection title="block-start">
        <p className="mt-3 text-sm text-muted-foreground">
          Use <code>align="block-start"</code> to position the addon above the textarea. Useful for
          toolbars with formatting controls or labels.
        </p>
        <Preview>
          <AlignBlockStart />
        </Preview>
      </DocsSection>

      <DocsSection title="block-end">
        <Preview>
          <p className="mt-3 text-sm text-muted-foreground">
            Use <code>align="block-end"</code> to position the addon below the textarea. Ideal for
            submit actions and composer toolbars.
          </p>
          <AlignBlockEnd />
        </Preview>
      </DocsSection>

      <DocsSection title="Icon">
        <Preview>
          <IconExample />
        </Preview>
      </DocsSection>

      <DocsSection title="Text">
        <Preview>
          <TextExample />
        </Preview>
      </DocsSection>

      <DocsSection title="Button">
        <Preview>
          <ButtonExample />
        </Preview>
      </DocsSection>

      <DocsSection title="Kbd">
        <Preview>
          <KbdExample />
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Place a native <code>{'<kbd>'}</code> element inside <code>DimeInputGroupAddon</code> to
          show a keyboard shortcut hint.
        </p>
      </DocsSection>

      <DocsSection title="Spinner">
        <Preview>
          <SpinnerExample />
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Use <code>DimeSpinner</code> inside an addon to indicate an async operation such as
          availability checking.
        </p>
      </DocsSection>

      <DocsSection title="Textarea">
        <Preview>
          <TextareaExample />
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Use <code>DimeInputGroupTextarea</code> with a <code>align="block-end"</code> addon to
          build a composer with a bottom toolbar.
        </p>
      </DocsSection>
    </div>
  );
}
